import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { mutate } from 'swr';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Badge from '@mui/material/Badge';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useIntl } from 'react-intl';

// project-imports
import Avatar from 'components/@extended/Avatar';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import MainCard from 'components/MainCard';
import SimpleBar from 'components/third-party/SimpleBar';
import { getNotificationFeedKey, getNotificationFeedStreamPath, NotificationFeedItem, useGetNotificationFeed } from 'api/notifications';
import { openSnackbar } from 'api/snackbar';
import { buildAuthenticatedEventStreamUrl } from 'utils/axios';

// assets
import { Danger, MessageText1, Notification, Setting2, StatusUp } from 'iconsax-reactjs';

// types

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function NotificationPage() {
  const intl = useIntl();
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const anchorRef = useRef<any>(null);
  const announcedNotificationIdsRef = useRef<Set<string>>(new Set());
  const [open, setOpen] = useState(false);
  const [markedReadAt, setMarkedReadAt] = useState<number>(0);
  const { notificationFeed, notificationFeedLoading, notificationFeedError } = useGetNotificationFeed();
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const unreadCount = useMemo(() => {
    if (!notificationFeed) {
      return 0;
    }

    if (!markedReadAt) {
      return notificationFeed.unreadCount;
    }

    return notificationFeed.items.filter((item) => {
      const occurredAt = new Date(item.occurredAt).getTime();
      return Number.isFinite(occurredAt) && occurredAt > markedReadAt;
    }).length;
  }, [markedReadAt, notificationFeed]);

  const handleMarkAllRead = () => {
    setMarkedReadAt(Date.now());
  };

  useEffect(() => {
    if (!notificationFeed?.items?.length) {
      return;
    }

    const announcedIds = announcedNotificationIdsRef.current;
    if (announcedIds.size === 0) {
      notificationFeed.items.forEach((item) => announcedIds.add(item.id));
      return;
    }

    notificationFeed.items
      .slice()
      .reverse()
      .forEach((item) => {
        if (announcedIds.has(item.id)) {
          return;
        }

        announcedIds.add(item.id);

        if (item.category !== 'command' && item.category !== 'knowledge') {
          return;
        }

        const title = formatNotificationTitle(item);

        openSnackbar({
          open: true,
          message: title + (item.serialNumber ? ` · ${item.serialNumber}` : ''),
          variant: 'alert',
          severity: item.severity,
          alert: { variant: 'filled' },
          action: false,
          close: true,
          actionButton: false,
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          transition: 'Fade',
          dense: false,
          maxStack: 3,
          iconVariant: 'usedefault'
        });
      });
  }, [intl, notificationFeed]);

  const renderAvatar = (item: NotificationFeedItem) => {
    switch (item.category) {
      case 'crash':
        return (
          <Avatar color="error" type="filled">
            <Danger size={20} variant="Bold" />
          </Avatar>
        );
      case 'ota':
        return (
          <Avatar color={item.severity === 'success' ? 'success' : item.severity === 'error' ? 'error' : 'warning'} type="filled">
            <Setting2 size={20} variant="Bold" />
          </Avatar>
        );
      case 'legacy-ota':
        return (
          <Avatar color="warning" type="filled">
            <Setting2 size={20} variant="Bold" />
          </Avatar>
        );
      case 'command':
        return (
          <Avatar color={item.severity === 'success' ? 'success' : item.severity === 'error' ? 'error' : 'info'} type="filled">
            <StatusUp size={20} variant="Bold" />
          </Avatar>
        );
      case 'battery':
        return (
          <Avatar color={item.severity === 'error' ? 'error' : 'warning'} type="filled">
            <StatusUp size={20} variant="Bold" />
          </Avatar>
        );
      case 'knowledge':
        return (
          <Avatar color={item.severity === 'warning' ? 'warning' : 'info'} type="filled">
            <MessageText1 size={20} variant="Bold" />
          </Avatar>
        );
      default:
        return (
          <Avatar type={item.severity === 'success' ? 'filled' : 'outlined'}>
            <StatusUp size={20} variant="Bold" />
          </Avatar>
        );
    }
  };

  const formatOccurredAt = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return intl.formatDate(parsed, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatNotificationTitle = (item: NotificationFeedItem) =>
    item.titleKey ? intl.formatMessage({ id: item.titleKey, defaultMessage: item.title }, item.titleValues) : item.title;

  const formatNotificationSummary = (item: NotificationFeedItem) =>
    item.summaryKey ? intl.formatMessage({ id: item.summaryKey, defaultMessage: item.summary }, item.summaryValues) : item.summary;

  const formatNotificationSource = (item: NotificationFeedItem) =>
    item.sourceLabelKey ? intl.formatMessage({ id: item.sourceLabelKey, defaultMessage: item.sourceLabel }) : item.sourceLabel;

  useEffect(() => {
    const streamPath = getNotificationFeedStreamPath();
    const streamUrl = buildAuthenticatedEventStreamUrl(streamPath);
    if (!streamUrl) {
      return;
    }

    const eventSource = new EventSource(streamUrl);

    const handleNotificationEvent = (event: MessageEvent<string>) => {
      try {
        const item = JSON.parse(event.data) as NotificationFeedItem;
        if (!item?.id) {
          return;
        }

        announcedNotificationIdsRef.current.add(item.id);

        if (item.category === 'command' || item.category === 'knowledge') {
          const title = formatNotificationTitle(item);
          openSnackbar({
            open: true,
            message: title + (item.serialNumber ? ` · ${item.serialNumber}` : ''),
            variant: 'alert',
            severity: item.severity,
            alert: { variant: 'filled' },
            action: false,
            close: true,
            actionButton: false,
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            transition: 'Fade',
            dense: false,
            maxStack: 3,
            iconVariant: 'usedefault'
          });
        }

        mutate(getNotificationFeedKey());
      } catch {
        mutate(getNotificationFeedKey());
      }
    };

    eventSource.addEventListener('notification-event', handleNotificationEvent);
    eventSource.onerror = () => {
      // EventSource automatically reconnects. Keep the UI quiet and let SWR remain the fallback.
    };

    return () => {
      eventSource.removeEventListener('notification-event', handleNotificationEvent);
      eventSource.close();
    };
  }, [intl]);

  return (
    <Box sx={{ flexShrink: 0, ml: 0.5 }}>
      <IconButton
        color="secondary"
        variant="light"
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        size="large"
        sx={(theme) => ({
          p: 1,
          color: 'secondary.main',
          bgcolor: open ? 'secondary.200' : 'secondary.100',
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.paper' : 'background.default' })
        })}
      >
        <Badge badgeContent={unreadCount} color="success" slotProps={{ badge: { sx: { top: 2, right: 4 } } }}>
          <Notification variant="Bold" />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.vars.customShadows.z1, borderRadius: 1.5, width: { xs: 320, sm: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} content={false}>
                  <CardContent>
                    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="h5">{intl.formatMessage({ id: 'sentinel.notifications.title', defaultMessage: 'Notifications' })}</Typography>
                      <Link component="button" type="button" onClick={handleMarkAllRead} variant="h6" color="primary" underline="hover">
                        {intl.formatMessage({ id: 'sentinel.notifications.mark_all_read', defaultMessage: 'Mark all read' })}
                      </Link>
                    </Stack>
                    <SimpleBar style={{ maxHeight: 'calc(100vh - 180px)' }}>
                      {notificationFeedLoading ? (
                        <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 6 }}>
                          <CircularProgress size={28} />
                          <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({ id: 'sentinel.notifications.loading', defaultMessage: 'Loading admin activity...' })}
                          </Typography>
                        </Stack>
                      ) : null}

                      {!notificationFeedLoading && notificationFeedError ? (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {intl.formatMessage({
                            id: 'sentinel.notifications.load_error',
                            defaultMessage: 'Unable to load the Sentinel notification feed right now.'
                          })}
                        </Alert>
                      ) : null}

                      {!notificationFeedLoading && !notificationFeedError ? (
                        <List
                          component="nav"
                          sx={(theme) => ({
                            '& .MuiListItemButton-root': {
                              p: 1.5,
                              my: 1.5,
                              border: `1px solid ${theme.vars.palette.divider}`,
                              '&:hover': { bgcolor: 'primary.lighter', borderColor: 'primary.light' },
                              '&:hover .MuiAvatar-root': { bgcolor: 'primary.main', color: 'background.paper' }
                            }
                          })}
                        >
                          {(notificationFeed?.items ?? []).map((item) => (
                            <ListItem
                              key={item.id}
                              component={ListItemButton}
                              onClick={() => setOpen(false)}
                              {...({ component: RouterLink, to: item.route } as any)}
                            >
                              <ListItemAvatar>{renderAvatar(item)}</ListItemAvatar>
                              <ListItemText
                                disableTypography
                                primary={
                                  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', width: '100%' }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>
                                        {formatNotificationTitle(item)}
                                        {item.serialNumber ? (
                                          <Typography component="span" variant="subtitle1">
                                            {' '}
                                            · {item.serialNumber}
                                          </Typography>
                                        ) : null}
                                      </Typography>
                                      <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{
                                            overflowWrap: 'anywhere',
                                            whiteSpace: item.category === 'crash' ? 'pre-line' : 'normal',
                                            display: '-webkit-box',
                                            WebkitBoxOrient: 'vertical',
                                            WebkitLineClamp: item.category === 'crash' ? 3 : 'unset',
                                            overflow: 'hidden'
                                          }}
                                        >
                                          {formatNotificationSummary(item)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {formatNotificationSource(item)}
                                          {item.tenant ? ` · ${item.tenant}` : ''}
                                        </Typography>
                                      </Stack>
                                    </Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ flexShrink: 0, minWidth: 112, textAlign: 'right', pt: 0.25 }}
                                    >
                                      {formatOccurredAt(item.occurredAt)}
                                    </Typography>
                                  </Stack>
                                }
                              />
                            </ListItem>
                          ))}

                          {!notificationFeed?.items?.length ? (
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar type="outlined">
                                  <MessageText1 size={20} variant="Bold" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={<Typography variant="h6">No recent Sentinel alerts</Typography>}
                                secondary={intl.formatMessage({
                                  id: 'sentinel.notifications.empty',
                                  defaultMessage: 'Crashes, OTA issues, and live command events will show up here.'
                                })}
                              />
                            </ListItem>
                          ) : null}
                        </List>
                      ) : null}
                    </SimpleBar>
                    <Stack direction="row" sx={{ justifyContent: 'center', mt: 1.5 }}>
                      <Link component={RouterLink} to="/live-report" variant="h6" color="primary" onClick={() => setOpen(false)}>
                        {intl.formatMessage({ id: 'sentinel.notifications.view_live_report', defaultMessage: 'View live report' })}
                      </Link>
                    </Stack>
                  </CardContent>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
